import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import './TopNavD1.scss';

const parseRgb = (rgb) => {
  if (!rgb) return null;
  const str = String(rgb).trim();
  const match = str.match(/rgba?\s*\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (!match) return null;
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
};

const getReadableTextColor = (rgb) => {
  const c = parseRgb(rgb);
  if (!c) return '#111';
  const srgb = [c.r, c.g, c.b]
    .map((v) => v / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  const luminance = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  return luminance > 0.55 ? '#111' : '#fff';
};

const getFreeShippingThreshold = (envios) => {
  if (!Array.isArray(envios) || envios.length === 0) return null;
  const mins = envios
    .map((e) => Number(e?.monto_minimo || 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (mins.length === 0) return null;
  return Math.min(...mins);
};

const TopNavD1 = ({ client, envios, minimumPurchase }) => {
  const messages = useMemo(() => {
    const items = [];

    const hasEnvios = Array.isArray(envios) && envios.length > 0;

    items.push('Envíos a todo el país');

    const minPurchase = Number(minimumPurchase || 0);
    if (Number.isFinite(minPurchase) && minPurchase > 0) {
      items.push(`Compra mínima: $${minPurchase.toLocaleString('es-AR')}`);
    } else {
      items.push('Comprá sin mínimo');
    }

    const freeFrom = getFreeShippingThreshold(envios);

    if (hasEnvios && freeFrom) items.push(`Envío gratis desde $${Number(freeFrom).toLocaleString('es-AR')}`);
    if (hasEnvios && !freeFrom) items.push('Envíos sin monto mínimo');

    return items.filter(Boolean);
  }, [envios, minimumPurchase]);

  const textColor = useMemo(
    () => getReadableTextColor(client?.color_sec?.rgb),
    [client?.color_sec?.rgb],
  );

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(!!mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [messages.length]);

  useEffect(() => {
    if (!messages || messages.length <= 1) return undefined;

    const intervalMs = reduceMotion ? 4500 : 3000;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [messages, reduceMotion]);

  if (!messages || messages.length === 0) return null;

  return (
    <div
      className="topnav-d1"
      style={{ background: 'var(--color-base-hover)', color: textColor }}
      data-testid="topnav-d1"
    >
      <div className="topnav-d1__viewport" aria-label="Avisos">
        <div className="topnav-d1__carousel" style={{ color: textColor }}>
          <span
            key={`${index}-${messages[index]}`}
            className="topnav-d1__message"
          >
            {messages[index]}
          </span>
        </div>
      </div>
    </div>
  );
};

TopNavD1.defaultProps = {
  client: null,
  envios: null,
  minimumPurchase: 0,
};

TopNavD1.propTypes = {
  client: PropTypes.shape({
    color_sec: PropTypes.shape({ rgb: PropTypes.string }),
  }),
  envios: PropTypes.arrayOf(PropTypes.shape({
    monto_minimo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  minimumPurchase: PropTypes.number,
};

export default TopNavD1;






